import { z } from 'zod';

/**
 * Plan policy — every licence-enforcement knob. Stored on `plans.policy` and
 * partially overridable per licence via `licences.policy_overrides`.
 * Nothing here is hardcoded in business logic: enforcement always reads the
 * merged policy at call time.
 */
export const PlanPolicySchema = z.object({
  /** Do staging activations consume a production slot? */
  stagingConsumesSlot: z.boolean().default(false),
  maxStagingEnvsPerSite: z.number().int().nonnegative().nullable().default(1),
  devEnvsUnlimited: z.boolean().default(true),

  allowLocalhost: z.boolean().default(true),
  allowLocalhostProduction: z.boolean().default(false),
  requireDomainVerification: z.boolean().default(true),
  domainWildcards: z.boolean().default(true),

  ipAllowlistEnabled: z.boolean().default(false),
  fingerprintRequired: z.boolean().default(false),
  fingerprintSelfReset: z.boolean().default(true),
  fingerprintResetsPer30Days: z.number().int().nonnegative().default(1),

  offlineFilesEnabled: z.boolean().default(false),
  validationIntervalHours: z.number().int().positive().default(24),
  offlineGraceHours: z.number().int().nonnegative().default(72),

  expiryGraceDays: z.number().int().nonnegative().default(7),
  reminderDaysBefore: z.array(z.number().int().positive()).default([30, 14, 7, 1]),
  autoSuspendOnPaymentFailureDays: z.number().int().nonnegative().default(7),
  autoSuspendOnExpiry: z.boolean().default(true),

  deactivationRelease: z
    .discriminatedUnion('type', [
      z.object({ type: z.literal('immediate') }),
      z.object({ type: z.literal('cooldown'), hours: z.number().int().positive() }),
      z.object({ type: z.literal('never') }),
    ])
    .default({ type: 'immediate' }),

  trialDays: z.number().int().positive().default(14),
});

export type PlanPolicy = z.infer<typeof PlanPolicySchema>;

export const DEFAULT_PLAN_POLICY: PlanPolicy = PlanPolicySchema.parse({});

/** Licence-level overrides: any subset of the policy. */
export const PolicyOverridesSchema = PlanPolicySchema.partial();
export type PolicyOverrides = z.infer<typeof PolicyOverridesSchema>;

/**
 * Merges a plan policy with licence-level overrides, key by key.
 *
 * Only keys actually present in `overrides` are applied. This matters because
 * `.partial()` makes fields optional but still fires their defaults, so parsing
 * an empty override object yields a complete default policy — applying that
 * wholesale would silently discard the plan's own settings.
 */
export function mergePolicy(planPolicy: unknown, overrides?: unknown): PlanPolicy {
  const base = PlanPolicySchema.parse(planPolicy ?? {});
  if (!overrides || typeof overrides !== 'object') return base;

  const providedKeys = new Set(Object.keys(overrides as Record<string, unknown>));
  if (!providedKeys.size) return base;

  const parsed = PolicyOverridesSchema.parse(overrides) as Record<string, unknown>;
  const applied: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (providedKeys.has(key) && value !== undefined) applied[key] = value;
  }

  return { ...base, ...applied } as PlanPolicy;
}
